<script>
  // Scrub-to-pick poster frame chooser. The paused <video> element is the live
  // preview (seeking a paused video shows the frame it lands on), and releasing
  // the slider captures that frame as the JPEG blob the "thumbnail" upload role
  // expects. Seeks are coalesced: a drag only ever queues the latest position
  // while the element is still busy seeking.
  //
  //   {#key file}<VideoThumbnailPicker source={file} onpick={({ blob }) => (thumbnailBlob = blob)} />{/key}
  //
  // `source` is a local File (upload flow) or the clip's CDN URL (editing an
  // existing moment); it's read once, so key the picker on it. A pick is emitted
  // as soon as the video loads, so callers always hold a blob without a drag.
  import {
    captureVideoFrame,
    clampThumbnailTime,
    isRemoteSource,
    THUMBNAIL_END_MARGIN_SECONDS,
    videoLoadErrorMessage
  } from '../../lib/video_file.js'

  let { source, initialSeconds = 0.1, onpick, onerror, disabled = false, currentThumbnailUrl = null } = $props()

  const STEP_SECONDS = 0.05
  const formatSeconds = (value) => `${value.toFixed(2)}s`

  let video = $state(null)
  let src = $state(null)
  let duration = $state(null)
  let seconds = $state(0)
  let pickedUrl = $state(null)
  let capturing = $state(false)
  let loadError = $state(null)

  // Slider position the element has not caught up to yet.
  let pendingSeek = null
  // Set when the next settled frame should be captured (slider released, or the
  // initial pick after metadata loads).
  let captureRequested = false

  const remote = $derived(isRemoteSource(source))
  const maxSeconds = $derived(duration === null ? 0 : Math.max(0, duration - THUMBNAIL_END_MARGIN_SECONDS))
  const ready = $derived(duration !== null && !loadError)

  $effect(() => {
    if (remote) {
      src = source
      return
    }
    const url = URL.createObjectURL(source)
    src = url
    return () => URL.revokeObjectURL(url)
  })

  $effect(() => () => {
    if (pickedUrl) URL.revokeObjectURL(pickedUrl)
  })

  const fail = (message) => {
    loadError = message
    onerror?.(new Error(message))
  }

  const capture = async () => {
    capturing = true
    try {
      const blob = await captureVideoFrame(video)
      if (pickedUrl) URL.revokeObjectURL(pickedUrl)
      pickedUrl = URL.createObjectURL(blob)
      onpick?.({ seconds: video.currentTime, blob })
    } catch (error) {
      onerror?.(error)
    } finally {
      capturing = false
    }
  }

  const seekTo = (at) => {
    if (!video) return
    if (video.seeking) {
      pendingSeek = at
      return
    }
    pendingSeek = null
    video.currentTime = at
  }

  const onseeked = () => {
    if (pendingSeek !== null) {
      const at = pendingSeek
      pendingSeek = null
      video.currentTime = at
      return
    }
    if (captureRequested) {
      captureRequested = false
      capture()
    }
  }

  const onloadedmetadata = () => {
    if (!isFinite(video.duration) || video.videoWidth === 0) {
      fail(videoLoadErrorMessage(source))
      return
    }
    duration = video.duration
    seconds = clampThumbnailTime(initialSeconds, video.duration)
    captureRequested = true
    seekTo(seconds)
  }

  const onSliderInput = (event) => {
    seconds = Number(event.target.value)
    seekTo(seconds)
  }

  const onSliderCommit = (event) => {
    const at = Number(event.target.value)
    captureRequested = true
    // Already on the frame (keyboard nudges, or a click without a drag): no
    // seeked event is coming, so capture straight away.
    if (!video.seeking && pendingSeek === null && Math.abs(video.currentTime - at) < 0.001) {
      captureRequested = false
      capture()
      return
    }
    seekTo(at)
  }
</script>

<fieldset class="fieldset">
  <legend class="fieldset-legend">Thumbnail</legend>
  <p class="label whitespace-normal">Drag to the frame you want as the poster, then release to pick it.</p>
  <div class="flex items-start gap-4 mt-1">
    <!-- svelte-ignore a11y_media_has_caption -->
    <video
      bind:this={video}
      crossorigin={remote ? 'anonymous' : undefined}
      {src}
      muted
      playsinline
      preload="auto"
      {onloadedmetadata}
      {onseeked}
      onerror={() => fail(videoLoadErrorMessage(source))}
      class="w-60 max-h-80 rounded-box bg-neutral shrink-0"
    ></video>
    <div class="flex flex-wrap gap-4">
      <div class="text-center">
        {#if pickedUrl}
          <img src={pickedUrl} alt="Picked thumbnail" class="h-24 w-24 object-cover rounded-box bg-base-200" />
        {:else}
          <div class="h-24 w-24 rounded-box bg-base-200"></div>
        {/if}
        <p class="text-xs text-base-content/60 mt-1">
          {capturing ? 'Capturing…' : pickedUrl ? `Picked · ${formatSeconds(seconds)}` : 'New'}
        </p>
      </div>
      {#if currentThumbnailUrl}
        <div class="text-center">
          <img src={currentThumbnailUrl} alt="Current thumbnail" class="h-24 w-24 object-cover rounded-box bg-base-200" />
          <p class="text-xs text-base-content/60 mt-1">Current</p>
        </div>
      {/if}
    </div>
  </div>

  {#if loadError}
    <p class="text-sm text-error mt-2">{loadError}</p>
  {:else}
    <input
      type="range"
      class="range range-primary range-sm w-full mt-3"
      aria-label="Thumbnail frame"
      min="0"
      max={maxSeconds}
      step={STEP_SECONDS}
      value={Math.min(seconds, maxSeconds)}
      disabled={disabled || !ready}
      oninput={onSliderInput}
      onchange={onSliderCommit}
    />
    <div class="flex justify-between text-xs text-base-content/60">
      <span>0.00s</span>
      <span>{ready ? formatSeconds(maxSeconds) : 'Loading…'}</span>
    </div>
  {/if}
</fieldset>
